import { GlassButton, GlassButtonDropdown } from "./GlassButtons";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup, GlassInputGroupTitle, GlassLayerSelect } from "./GlassInput";
import { useContext, useEffect, useMemo, useState } from "react";
import { MapitStateContext } from "src/states/MapitState";
import { LayerVariabilityType } from "src/common/managers/Types";
import { bbox, buffer } from "@turf/turf";
import { addPreviewLayer, removePreviewLayer } from 'src/utils/mapLayerHelpers';
import { DataCollector } from "src/dataCollector/DataCollector";
import { ProtectedFeature } from "./ProtectedFeature";
import { ApplicationStateContext, Feature, FeatureType } from "src/states/ApplicationState";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { message } from "./Message";
import { GeoJSONSource } from "maplibre-gl";
import { ConflictSearchParams } from "./ConflictSearchResults";
import { geomWithBuffer } from "./ConflictSearchCalculations";
import { AdvancedDragModal, ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { FeatureLayer } from "src/managers/WmsLayerFunc";

export function ConflictSearchDialog({ window }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { state: mapitState } = useContext(MapitStateContext);
   const [selectedLayer, setSelectedLayerId] = useState<number | undefined>(undefined);
   const [bufferDistance, setBufferDistance] = useState<number>(0);
   const [endpoint, setEndpoint] = useState<keyof typeof wfsEndpoints>("Matriklen2");
   const [whoAmI, setWhoAmI] = useState<string | null>(null);
   const [featureType, setFeatureType] = useState<keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"]>();
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const accessibleProfiles = useMemo(() => profiles.filter(p => !p.feature || hasAccessToFeature(p.feature)), [hasAccessToFeature]);
   const [selectedProfile, setSelectedProfileId] = useState<ConflictProfile<WFSEndpointsType>>(() => accessibleProfiles[0]);

   const [searchMode, setSearchMode] = useState<'profile' | 'datasource' | 'favoritelayer' | 'activelayers'>('profile');
   const selectableLayers = useMemo(() => {
      let options = Object.values(mapitState.layers).filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer);
      setSelectedLayerId((prev) => prev ? prev : options.length > 0 ? options[0].layerId : undefined);
      return options;
   }, [mapitState.layers])

   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         addPreviewLayer(map, "conflict-search-area");
         return () => {
            removePreviewLayer(map, "conflict-search-area");
         }
      }
   }, [mapitState.map]);

   useEffect(() => {
      if (mapitState.map && selectedLayer) {
         let geom = mapitState.layers[selectedLayer]?.geoJson;
         let map = mapitState.map.getMapPop();
         (map.getSource("conflict-search-area") as GeoJSONSource)?.setData(geomWithBuffer(geom, bufferDistance) as any);
      }
   }, [mapitState.map, selectedLayer, bufferDistance]);

   useEffect(() => {
      if (mapitState.map) {
         let map = mapitState.map.getMapPop();
         map.moveLayer("conflict-search-area-fill")
         map.moveLayer("conflict-search-area-outline")
         map.moveLayer("conflict-search-area-linestring")
         map.moveLayer("conflict-search-area-point")
      }
   }, [mapitState.map, mapitState.layers]);

   async function handleClickSingleSearch() {
      handleClickProfileSearch({
         id: "single-search",
         name: "Single layer search",
         feature: Feature.ConflictSearch,
         layers: [{ source: endpoint, layer: featureType! }]
      });
   }

   async function handleClickProfileSearch(selectedProfile: Omit<ConflictSearchParams, "geom" | "buffer">) {
      if (!selectedProfile) {
         message.error(Localization.getText("Please select a profile"));
         return
      }
      if (!selectedLayer) {
         message.error(Localization.getText("Please select a layer to search"));
         return;
      }
      let searchParams: ConflictSearchParams = {
         ...selectedProfile,
         geom: mapitState.layers[selectedLayer]?.geoJson,
         buffer: bufferDistance
      }
      windowDispatch(openWindow(WindowId.ConflictSearchResults, { searchParams }));
   }

   return (
      <AdvancedDragModal
         PosDefault={{ left: "98px", top: "60px", width: "320px", height: "400px" }}
         title={Localization.getText("Conflict search")}
         onClose={() => windowDispatch(closeWindow(window.id))}
      >
         <ADModalBody>
            <GlassInputGroup >
               <GlassInputControlLabel>{Localization.getText("layer")}</GlassInputControlLabel>
               <GlassLayerSelect selected={selectedLayer} onSelect={(e) => { setSelectedLayerId(e); }} layers={selectableLayers} />
               <GlassInputControlLabel>{Localization.getText("Buffer")}</GlassInputControlLabel>
               <GlassInputControl><input type="number" value={bufferDistance} onChange={(e) => setBufferDistance(Number(e.target.value))} /><span>m</span></GlassInputControl>
               <GlassInputControlLabel>{Localization.getText("Method")}</GlassInputControlLabel>
               <GlassInputControl>
                  <input type="radio" id="conflict-search-prof" value="profile" checked={searchMode === 'profile'} onChange={() => setSearchMode('profile')} />
                  <label htmlFor="conflict-search-prof">{Localization.getText("Layer profiles")}</label>
                  <input type="radio" id="conflict-search-data" value="datasource" checked={searchMode === 'datasource'} onChange={() => setSearchMode('datasource')} />
                  <label htmlFor="conflict-search-data">{Localization.getText("Single layer")}</label>
                  {/* <input type="radio" id="conflict-search-favorite" value="favoritelayer" checked={searchMode === 'favoritelayer'} onChange={() => setSearchMode('favoritelayer')} /> */}
                  {/* <label htmlFor="conflict-search-favorite">{Localization.getText("Favorite layers")}</label> */}
                  <input type="radio" id="conflict-search-active" value="activelayers" checked={searchMode === 'activelayers'} onChange={() => setSearchMode('activelayers')} />
                  <label htmlFor="conflict-search-active">{Localization.getText("Active layers")}</label>
               </GlassInputControl>

               {/* Profile controls (only visible when searchMode === 'profile') */}
               {searchMode === 'profile' && (
                  <>
                     <GlassInputGroupTitle>{Localization.getText("Layer profiles")}</GlassInputGroupTitle>
                     <GlassInputControlLabel>{Localization.getText("Layer profile")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={selectedProfile.id} onChange={(e) => {
                           let profile = accessibleProfiles.find(p => p.id === e.target.value)
                           setSelectedProfileId(profile!);
                        }}>
                           {accessibleProfiles.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                        </select>
                     </GlassInputControl>
                  </>
               )}

               {/* Datasource controls (only visible when searchMode === 'datasource') */}
               {searchMode === 'datasource' && (
                  <>
                     <GlassInputGroupTitle>{Localization.getText("Single layer")}</GlassInputGroupTitle>
                     <GlassInputControlLabel>{Localization.getText("Register")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={endpoint} onChange={(e) => {
                           setEndpoint(e.target.value as keyof typeof wfsEndpoints)
                           setFeatureType(wfsEndpoints[e.target.value]?.featureTypes[0])
                        }}>
                           {Object.keys(wfsEndpoints).filter((a) => wfsEndpoints[a].active !== false).map((key) => (
                              <option key={key} value={key}>{key}</option>
                           ))}
                        </select>
                     </GlassInputControl>
                     {wfsEndpoints[endpoint]?.["whoAmI"] ?
                        <>
                           <GlassInputControlLabel>{Localization.getText("Email")}</GlassInputControlLabel>
                           <GlassInputControl>
                              <input type="email" value={whoAmI ?? ""} onChange={(e) => setWhoAmI(e.target.value)} />
                           </GlassInputControl>
                        </>
                        : <></>
                     }

                     <GlassInputControlLabel>{Localization.getText("Layer")}</GlassInputControlLabel>
                     <GlassInputControl>
                        <select value={featureType} onChange={(e) => setFeatureType(e.target.value as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"])}>
                           {Object.keys(wfsEndpoints[endpoint]?.featureTypes ?? {}).map((key) => (
                              <option key={key} value={key}>{key}</option>
                           ))}
                        </select>
                     </GlassInputControl>
                  </>
               )}
            </GlassInputGroup>

         </ADModalBody>
         <ADModalFooter >

            {searchMode === 'datasource' && SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
               <ProtectedFeature feature={Feature.Debugging} contentsIfNoAccess={<></>} >
                  <GlassButtonDropdown placement={"bottom-start"} onSelect={async (action) => {
                     switch (action) {
                        case "GetCapabilities":
                           console.log(await DataCollector.WFS.getCapabilities(endpoint))
                           break;
                        case "DescribeFeatureType":
                           console.log(await DataCollector.WFS.describeFeatureType(endpoint, featureType!))
                           break;
                        case "GetFeatureBBOX":
                           if (selectedLayer) {
                              let x = buffer(mapitState.layers[selectedLayer]?.geoJson, bufferDistance || 0, { units: 'meters' });
                              if (x) {
                                 console.log(
                                    await DataCollector.WFS.getFeatureBBOX(endpoint, featureType!, bbox(x).flat() as [number, number, number, number])
                                 );
                              }
                           }
                           break;
                     }
                  }} options={[
                     { text: "GetCapabilities", value: "GetCapabilities" },
                     { text: "DescribeFeatureType", value: "DescribeFeatureType" },
                     { text: "GetFeatureBBOX", value: "GetFeatureBBOX" },
                  ]} >
                     Debug
                  </GlassButtonDropdown>
               </ProtectedFeature>
               : <></>
            }
            <GlassButton disabled={!selectedProfile || !selectedLayer} onClick={() => {
               if (searchMode === 'profile') {
                  if (!selectedProfile) message.error(Localization.getText("Please select a profile"));
                  handleClickProfileSearch(selectedProfile);
               } else if (searchMode === 'datasource') {
                  handleClickSingleSearch()
               } else if (searchMode === 'favoritelayer') {

               } else if (searchMode === 'activelayers') {
                  let activeKeys = mapitState.selectedFeatureLayerKeys;
                  let allLayers = mapitState.featureLayers;
                  let layerProfile = createLayerFromActiveLayers(activeKeys, allLayers)
                  handleClickProfileSearch(layerProfile);
               }
            }}>{Localization.getText("Find conflicts")}</GlassButton>

         </ADModalFooter>
      </AdvancedDragModal >
   );
}



// MARK: Profiles
export type WFSEndpointsType = { source: string, layer: string, title?: string };
export type ConflictProfile<T> = {
   id: string; name: string; layers: T[]; feature?: FeatureType,
   pages?: Omit<ConflictProfile<T>, "feature" | "id" | "pages">[]
};
const profiles: ConflictProfile<WFSEndpointsType>[] = [
   { id: "profile_test", name: "Test Profile", layers: [{ source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" }, { source: "Matriklen2", layer: "Skelpunkt_Gaeldende" }], feature: Feature.Debugging },
   { id: "profile_error", name: "Profile with error layer", layers: [{ source: "Matriklen2", layer: "XXSamletFastEjendom_Gaeldende" }, { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" }, { source: "Matriklen2", layer: "XXSkelpunkt_Gaeldende" }], feature: Feature.Debugging },
   {
      id: "profile_CTO",
      name: "Bæredygtig Udvikling",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Vejdirektoratet (Statsvej)", layer: "Vejbyggelinje(Statsvej)", title: "Vejbyggelinje (Statsvej)" },
         { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
         { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
         { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
         { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
         { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
         { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
         { source: "Matriklen2", layer: "KlitfredningFlade_Gaeldende", title: "KlitfredningFlade" },
         { source: "Matriklen2", layer: "StrandbeskyttelseFlade_Gaeldende", title: "Strandbeskyttelsesflade" },
         { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper" },
         { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
         { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
         { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
         { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "Fredede fortidsminder" },
         { source: "Plandata.dk", layer: "theme-knz-kystnaerhedszone-polygon", title: "Kystnærhedszone" },
      ]
   },
   {
      id: "profile_Landbrugsmæglere",
      name: "Landbrugsmæglere",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Matriklen2", layer: "SamletFastEjendom_Gaeldende", title: "Samlet fast ejendom" },
         { source: "Matriklen2", layer: "Matrikelskel_Gaeldende", title: "Matrikelskel" },
         { source: "Matriklen2", layer: "Skelpunkt_Gaeldende", title: "Skelpunkt" },
         { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
         { source: "Miljøgis", layer: "vp3e2022_Nitratfølsomme indvindingsområder", title: "Nitratfølsomme indvindingsområder" },
         { source: "Miljø Portal", layer: "2142 Kommunernes status for boringsnære beskyttelsesområder (BNBO)", title: "Kommunernes status for boringsnære beskyttelsesområder (BNBO)" },
         { source: "Miljøgis", layer: "indsatsomraader", title: "Indsatsområder" },
         { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
         { source: "Miljøgis", layer: "indvindingsoplande_alle", title: "Indvindingsoplande, Alle" },
         { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
         { source: "Miljø Portal", layer: "2014 Beskyttede vandløb", title: "Beskyttede vandløb" },
         { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
         { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
         { source: "Miljøgis", layer: "NP2-2016 Ramsarområder", title: "Ramsarområder NP2" },
         { source: "Miljø Portal", layer: "2020 Ramsar områder", title: "Ramsar områder" },
         { source: "Miljøgis", layer: "np11fredede_omraader_dai", title: "Fredede områder NP11" },
         { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
         { source: "Miljø Portal", layer: "2140 Bekendtgørelsesfredninger", title: "Bekendtgørelsesfredninger" },
         { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "GLM Fortidsminder" },
         { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
         { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
         { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
         { source: "Miljø Portal", layer: "2013 Beskyttede naturtyper", title: "Beskyttede naturtyper" },
         { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
         { source: "Matriklen2", layer: "StrandbeskyttelseLinje_Gaeldende", title: "Strandbeskyttelse" },
         { source: "Matriklen2", layer: "KlitfredningLinje_Gaeldende", title: "Klitfredning" },
         { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
         { source: "Miljøgis", layer: "Naturmæssig særlig værdifuld skov", title: "Naturmæssig særlig værdifuld skov" },
         { source: "Miljøgis", layer: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)", title: "Naturmæssigt særlig værdifuld skov på offentlige arealer (§25-skov)" },
         { source: "FVM (Føde, Land og Fisk)", layer: "Jordbundskort_2024", title: "Jordbundskort" },
         { source: "Miljø Portal", layer: "2055 Råstofområder", title: "Råstofområder" },
         { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
         { source: "DKJord", layer: "Jordforurening, nuanceret", title: "Jordforurening, nuanceret" },
         { source: "Miljø Portal", layer: "2097 Igangværende påbud JFL", title: "Igangværende påbud JFL" },
         { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_vedtaget", title: "Planlagt trafikanlæg vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_planlagttrafikanlaeg_forslag", title: "Planlagt trafikanlæg forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_vaerdifuldtlandbrugsomraade_vedtaget", title: "Værdifuldt landbrugsområde vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget", title: "Lokalplan vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag", title: "Lokalplan forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_vedtaget_v", title: "Kommuneplanramme vedtaget" },
         { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_forslag_v", title: "Kommuneplanramme forslag" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag_vindmoelle", title: "Lokalplan forslag Vindmølle" },
         { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget_vindmoelle", title: "Lokalplan vedtaget Vindmølle" },
      ]
   },
   {
      id: "profile_sgav",
      name: "SGAV - MARS",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "MARS SGAV", layer: "Naturpotentialer" },
         { source: "MARS SGAV", layer: "Projekter - Samlet" },
         { source: "MARS SGAV", layer: "Projekter - Tilskudsordning - Skovrejsning" },
         { source: "MARS SGAV", layer: "Skitseprojekter - Samlet" }
      ]
   },
   {
      id: "Grundsvandsprofilen",
      name: "Grundvand-profilen",
      feature: Feature.ConflictSearch,
      layers: [
         { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
         { source: "Miljøgis", layer: "saarbaregrundvandsdannendeomraader", title: "Sårbare grundvandsdannende områder" },
         { source: "Miljøgis", layer: "bnbo", title: "Boringsnære beskyttelsesområder" },
         { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
         { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
         { source: "Miljø Portal", layer: "2112 Kommunale indsatsplaner for grundvandsbeskyttelse", title: "Kommunale indsatsplaner for grundvandsbeskyttelse" },
      ]
   },
   {
      id: "profile_NyboligLandbrug",
      name: "Landbrugsmæglere (udvidet)",
      feature: Feature.ConflictSearch,
      layers: [],
      pages: [
         {
            name: "01 Matrikelkort og optaget vej", layers: [
               { source: "Matriklen2", layer: "Matrikelskel_Gaeldende" },
               { source: "Matriklen2", layer: "OptagetVej_Gaeldende" }
            ]
         },
         {
            name: "02 Lokalplan",
            layers: [
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget", title: "Lokalplan vedtaget" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag", title: "Lokalplan forslag" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_vedtaget_vindmoelle", title: "Lokalplan vedtaget Vindmølle" },
               { source: "Plandata.dk", layer: "theme_pdk_lokalplan_forslag_vindmoelle", title: "Lokalplan forslag Vindmølle" },
            ]
         },
         {
            name: "03 Kommuneplan",
            layers: [
               { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_vedtaget_v", title: "Kommuneplanramme vedtaget" },
               { source: "Plandata.dk", layer: "theme_pdk_kommuneplanramme_forslag_v", title: "Kommuneplanramme forslag" },
            ]
         },
         {
            name: "04 Bygge og beskyttelseslinjer", layers: [
               { source: "Miljø Portal", layer: "2008 Kirkebyggelinjer", title: "Kirkebyggelinjer" },
               { source: "Miljø Portal", layer: "2005 Skovbyggelinjer", title: "Skovbyggelinjer" },
               { source: "Miljø Portal", layer: "2003 Søbeskyttelseslinjer", title: "Søbeskyttelseslinjer" },
               { source: "Miljø Portal", layer: "2004 Åbeskyttelseslinjer", title: "Åbeskyttelseslinjer" },
               { source: "Plandata.dk", layer: "theme-knz-kystnaerhedszone-polygon", title: "Kystnærhedszone" },
            ]
         },
         {
            name: "05 Beskyttede diger og fortidsminder", layers: [
               { source: "Miljø Portal", layer: "2002 Beskyttede sten- og jorddiger", title: "Beskyttede sten- og jorddiger" },
               { source: "FVM (Føde, Land og Fisk)", layer: "GLM_Fortidsminder_2026", title: "GLM Fortidsminder" },

            ]
         },
         {
            name: "06 Fredede områder", layers: [
               { source: "Miljøgis", layer: "np11fredede_omraader_dai", title: "Fredede områder NP11" },
               { source: "Miljø Portal", layer: "2009 Fredede områder", title: "Fredede områder" },
               { source: "Miljø Portal", layer: "2140 Bekendtgørelsesfredninger", title: "Bekendtgørelsesfredninger" },
            ]
         },
         {
            name: "07 Kulturarv bygninger", layers: [
               { source: "Plandata.dk", layer: "theme_pdk_kulturhistoriskbevaringsvaerdi_vedtaget", title: "Kulturhistorisk bevaringsværdi vedtaget" }
            ]
         },
         {
            name: "08 Fredskov og klit-strandbeskyttelse", layers: [
               { source: "Matriklen2", layer: "StrandbeskyttelseLinje_Gaeldende", title: "Strandbeskyttelse" },
               { source: "Matriklen2", layer: "KlitfredningLinje_Gaeldende", title: "Klitfredning" },
               { source: "Miljøgis", layer: "Fredskov", title: "Fredskov" },
            ]
         },
         {
            name: "10 Natura 2000 og Ramsar områder", layers: [
               { source: "Miljø Portal", layer: "2018 NATURA 2000 - Fuglebeskyttelse", title: "NATURA 2000 - Fuglebeskyttelse" },
               { source: "Miljø Portal", layer: "2019 NATURA 2000 - Habitatområder", title: "NATURA 2000 - Habitatområder" },
               { source: "Miljøgis", layer: "NP2-2016 Ramsarområder", title: "Ramsarområder NP2" },
               { source: "Miljø Portal", layer: "2020 Ramsar områder", title: "Ramsar områder" },
               { source: "Miljøgis", layer: "Natur- og vildtreservater", title: "Natur- og vildtreservater" }
            ]
         },
         {
            name: "11 BNBO og grundvandsbeskyttelse", layers: [
               { source: "Miljøgis", layer: "vp3e2022_Nitratfølsomme indvindingsområder", title: "Nitratfølsomme indvindingsområder" },
               { source: "Miljø Portal", layer: "2142 Kommunernes status for boringsnære beskyttelsesområder (BNBO)", title: "Kommunernes status for boringsnære beskyttelsesområder (BNBO)" },
            ]
         },
         {
            name: "12 Retentionskort", layers: [
               { source: "Miljøgis", layer: "dk_retentionskort_2020", title: "Retentionskort" }
            ]
         },
         {
            name: "13 Jordforurening", layers: [
               { source: "DKJord", layer: "Jordforurening, V1 kortlagt", title: "Jordforurening, V1 kortlagt" },
               { source: "DKJord", layer: "Jordforurening, V2 kortlagt", title: "Jordforurening, V2 kortlagt" },
               { source: "DKJord", layer: "Jordforurening, nuanceret", title: "Jordforurening, nuanceret" },
               { source: "Miljø Portal", layer: "2097 Igangværende påbud JFL", title: "Igangværende påbud JFL" },
            ]
         },
         {
            name: "14 Drikkevand", layers: [
               { source: "Miljøgis", layer: "drikkevandsinteresser", title: "Drikkevandsinteresser" },
               { source: "Miljøgis", layer: "indvindingsoplande_alle", title: "Indvindingsoplande, Alle" },
               { source: "Miljøgis", layer: "foelsommeindvindingsomraader", title: "Følsomme indvindingsområder" },
               { source: "Miljøgis", layer: "indvindingsoplande_udenfor_osd", title: "Indvindingsoplande udenfor OSD" },
               { source: "Miljøgis", layer: "indsatsomraader", title: "Indsatsområder" },

            ]
         },
         {
            name: "15 Marker og markblokke 2025", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Marker_2025", title: "Marker 2025" },
               { source: "FVM (Føde, Land og Fisk)", layer: "Markblokke_2025", title: "Markblokke 2025" },
               { source: "FVM (Føde, Land og Fisk)", layer: "Markblokke_2026", title: "Markblokke 2026" },
            ]
         },
         {
            name: "16 Markinfo", layers: [

            ]
         },
         {
            name: "17 Jordbundskort 2024", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Jordbundskort_2024", title: "Jordbundskort" },
            ]
         },
         {
            name: "18 Vandområde planer", layers: [

            ]
         },
         {
            name: "19 Skovrejsning", layers: [
               { source: "Plandata.dk", layer: "theme_pdk_skovrejsningsomraade_forslag", title: "Skovrejsningsområde forslag" },
               { source: "Plandata.dk", layer: "theme_pdk_skovrejsningsomraade_vedtaget", title: "Skovrejsningsområde vedtaget" },
               { source: "Miljøgis", layer: "Arealkort 2022 (digital skovkort)", title: "Arealkort 2022 (digital skovkort)" }
            ]
         },
         {
            name: "20 Skov HNV naturværdi", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "HNV_skov_2024" },
            ]
         },
         {
            name: "21 Arealanvendelse 2024 og markkrat 2025", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Arealanvendelse_2024", title: "Arealanvendelse 2024" },
               { source: "FVM (Føde, Land og Fisk)", layer: "Markkrat_2026", title: "Markkrat 2026" }
            ]
         },
         {
            name: "26 Vandboringer", layers: [
               { source: "FVM (Føde, Land og Fisk)", layer: "Vandboringer_i_IMK_2024", title: "Vandboringer i IMK 2024" }]
         }
      ]
   }
]


function createLayerFromActiveLayers(activeKeys: string[], allLayers: Record<string, FeatureLayer>): Omit<ConflictSearchParams, "geom" | "buffer"> {
   let layers = activeKeys.map(key => {
      let layer = allLayers[key].layer;
      if ("wfs" in layer && layer.wfs && layer.wfs["key"] && layer.wfs["type"]) {
         return { source: layer.wfs["key"], layer: layer.wfs["type"], title: key.split("\\").at(-1) }
      }
      return undefined
   }).filter(l => l !== undefined);

   return {
      id: "Active Layers", name: "Aktive lag", layers: layers, feature: Feature.BasicMap,
   }
}
